/**
 * R17 Payments — reframed 2026-07-17 (user decision: no in-app checkout until
 * the real gateway phase). Members see dues + breakdown + HOW TO PAY (the
 * society's bank/UPI details) + history; they can never mark a bill paid.
 * Receipt is recorded by the HON. TREASURER (bye-law 113) — anyone holding
 * write on money.payments_record sees a "Record payments" worklist here:
 * unpaid bills across the society → mark received with method + reference.
 * Desktop (≥1024): bill table (left) + due card & how-to-pay (right).
 * Wired: GET /api/payments/current · /pay-info · /history · /outstanding,
 * POST /api/payments/<id>/record. The old mock POST /pay is retired.
 */
import { LinearGradient } from 'expo-linear-gradient';
import { router } from 'expo-router';
import React from 'react';
import { Platform, StyleSheet, View, useWindowDimensions } from 'react-native';

import { formatINR, radius, space } from '@bero/tokens';
import {
  billPillLabel, billPillStatus, inr, invoiceCode, monthYear, shortDate,
} from '@/components/money/bits';
import {
  errorMessage, fetchBillHistory, fetchCurrentBill, fetchOutstanding, fetchPayTo,
  recordPayment, useAsync, type Bill, type OutstandingBill, type PayTo,
} from '@/components/money/api';
import { Icon } from '@/components/money/icons';
import { useT } from '@/i18n';
import { api } from '@/lib/client';
import { useSession } from '@/lib/session';
import { BrandBar } from '@/components/shell/BrandBar';
import { useTheme } from '@/theme';
import {
  AppText, Button, Card, Chip, CountUp, EmptyState, Field, Hairline, Input,
  Pill, Screen, Sheet, Skeleton,
} from '@/ui';

const WIDE = 1024;
const RECORD_METHODS = ['UPI', 'NEFT', 'CASH', 'CHEQUE', 'CARD'] as const;

export default function PayTab() {
  const { colors } = useTheme();
  const { t } = useT();
  const { user } = useSession();
  // Payment-method chip labels: UPI/NEFT are acronyms (kept as-is), the rest translate.
  const methodLabel: Record<string, string> = {
    UPI: 'UPI', NEFT: 'NEFT', CASH: t('pay_method_cash'), CHEQUE: t('pay_method_cheque'), CARD: t('pay_method_card'),
  };
  const { width } = useWindowDimensions();
  const wide = Platform.OS === 'web' && width >= WIDE;
  const { data: bill, loading: billLoading, error: billError, refetch: refetchBill } = useAsync(fetchCurrentBill, []);
  const { data: history, loading: historyLoading, refetch: refetchHistory } = useAsync(() => fetchBillHistory(wide ? 12 : 6), [wide]);
  const { data: payTo } = useAsync(fetchPayTo, []);

  const [sheetOpen, setSheetOpen] = React.useState(false);

  // Treasurer worklist — only holders of write on money.payments_record.
  const [canRecord, setCanRecord] = React.useState(false);
  const [outstanding, setOutstanding] = React.useState<OutstandingBill[] | null>(null);
  const [recordFor, setRecordFor] = React.useState<OutstandingBill | null>(null);
  const [method, setMethod] = React.useState<string>('UPI');
  const [reference, setReference] = React.useState('');
  const [recordBusy, setRecordBusy] = React.useState(false);
  const [recordNotice, setRecordNotice] = React.useState<string | null>(null);

  const loadWorklist = React.useCallback(async () => {
    try {
      const me = await api.get<{ perms?: Record<string, string[]> }>('/api/positions/me');
      if (!(me.perms?.['money.payments_record'] ?? []).includes('write')) return;
      setCanRecord(true);
      setOutstanding(await fetchOutstanding());
    } catch {
      // not a treasurer — section stays hidden.
    }
  }, []);
  React.useEffect(() => { void Promise.resolve().then(() => loadWorklist()); }, [loadWorklist]);

  const doRecord = () => {
    if (!recordFor || recordBusy) return;
    setRecordBusy(true);
    recordPayment(recordFor.id, method, reference.trim())
      .then(() => {
        setRecordNotice(t('pay_recorded_ok', { amount: inr(recordFor.totalAmount), flat: recordFor.flatCode ?? t('pay_the_flat') }));
        setRecordFor(null);
        setReference('');
        void loadWorklist();
        void refetchBill();
        void refetchHistory();
      })
      .catch((e) => {
        setRecordNotice(errorMessage(e, t('pay_record_error')));
        setRecordFor(null);
      })
      .finally(() => setRecordBusy(false));
  };

  const PayToCard = payTo ? (
    <Card style={{ marginTop: space.s4 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
        <Icon name="landmark" size={16} color={colors.textSecondary} />
        <AppText variant="label" tone="tertiary">{t('pay_how_to_pay')}</AppText>
      </View>
      <View style={{ marginTop: space.s3, gap: space.s2 }}>
        {payTo.upiVpa ? <PayToRow label="UPI" value={payTo.upiVpa} /> : null}
        {payTo.bankName ? <PayToRow label={t('pay_bank')} value={payTo.bankName} /> : null}
        {payTo.bankAccount ? <PayToRow label={t('pay_account')} value={payTo.bankAccount} /> : null}
        {payTo.bankIfsc ? <PayToRow label="IFSC" value={payTo.bankIfsc} /> : null}
      </View>
      <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s3 }}>
        {t('pay_how_to_pay_note')}
      </AppText>
    </Card>
  ) : null;

  const TreasurerSection = canRecord ? (
    <View style={wide ? { paddingHorizontal: space.s6 } : undefined}>
      <AppText variant="label" tone="tertiary" style={{ marginTop: space.s8, marginBottom: 10 }}>
        {t('pay_record_hdr')}
      </AppText>
      {recordNotice ? (
        <AppText variant="bodySm" weight="500" style={{ marginBottom: space.s2, color: colors.textPrimary }}>
          {recordNotice}
        </AppText>
      ) : null}
      {outstanding === null ? (
        <Skeleton h={56} />
      ) : outstanding.length === 0 ? (
        <AppText variant="bodySm" tone="tertiary">{t('pay_no_unpaid')}</AppText>
      ) : (
        <Card>
          {outstanding.map((b, i) => (
            <View key={b.id}>
              {i > 0 ? <Hairline /> : null}
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, paddingVertical: space.s2 }}>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <AppText variant="bodyMd" weight="600">
                    {b.flatCode ?? t('pay_flat')} · {monthYear(b.period)}
                  </AppText>
                  <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                    {t('pay_due', { date: shortDate(b.dueDate) })}{b.lateFee > 0 ? ` · ${t('pay_includes_late', { amount: inr(b.lateFee) })}` : ''}
                  </AppText>
                </View>
                <AppText variant="bodyMd" weight="500" mono>{inr(b.totalAmount)}</AppText>
                <Button title={t('pay_received')} onPress={() => {
                  setRecordFor(b);
                  setMethod('UPI');
                  setReference('');
                  setRecordNotice(null);
                }} />
              </View>
            </View>
          ))}
        </Card>
      )}
    </View>
  ) : null;

  return (
    <View style={{ flex: 1, backgroundColor: colors.canvas0 }}>
      <BrandBar title={t('pay_title')} />
      <Screen padded={!wide} style={wide ? undefined : { paddingTop: space.s5 }}>
        {billLoading ? (
          <View style={wide ? { padding: space.s6 } : undefined}>
            <Card style={{ padding: space.s6, borderRadius: 16 }}>
              <Skeleton w="40%" h={12} />
              <Skeleton w="60%" h={38} style={{ marginTop: 10 }} />
              <Skeleton w="50%" h={14} style={{ marginTop: 10 }} />
            </Card>
          </View>
        ) : billError ? (
          <View style={wide ? { padding: space.s6 } : undefined}>
            <EmptyState
              title={t('pay_err_title')}
              body={t('pay_err_body')}
              cta={t('retry')}
              onCta={() => void refetchBill()}
            />
          </View>
        ) : !bill ? (
          <View style={wide ? { padding: space.s6 } : undefined}>
            <EmptyState
              icon={<Icon name="check-circle" size={40} color={colors.primary500} />}
              title={t('pay_no_dues_title')}
              body={t('pay_no_dues_body')}
            />
            {PayToCard}
          </View>
        ) : bill.status === 'PAID' ? (
          <View style={wide ? { padding: space.s6 } : undefined}>
            <Card style={{ padding: space.s6, borderRadius: 16, alignItems: 'center' }}>
              <Icon name="check-circle" size={40} color={colors.primary500} />
              <AppText variant="headingSm" weight="500" style={{ marginTop: space.s3 }}>
                {t('pay_fully_paid', { month: monthYear(bill.period) })}
              </AppText>
              <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 4 }}>
                {bill.payments[0] ? t('pay_recorded_on', { date: shortDate(bill.payments[0].paidAt) }) : t('pay_no_outstanding')}
              </AppText>
            </Card>
            {PayToCard}
          </View>
        ) : wide ? (
          <DesktopPayments
            bill={bill}
            history={history ?? []}
            historyLoading={historyLoading}
            payToCard={PayToCard}
          />
        ) : (
          <>
            <HeroBand bill={bill} flatCode={user?.flat?.code} />
            <View style={{ flexDirection: 'row', gap: space.s2, marginTop: space.s4 }}>
              <Button title={t('pay_view_breakdown')} kind="secondary" onPress={() => setSheetOpen(true)} style={{ flex: 1 }} />
              <Button title={t('pay_past_bills')} kind="secondary" onPress={() => router.push('/bills-history')} style={{ flex: 1 }} />
            </View>
            {PayToCard}
          </>
        )}

        {TreasurerSection}

        {/* recent payments */}
        <View style={wide ? { paddingHorizontal: space.s6 } : undefined}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: space.s8, marginBottom: 4 }}>
            <AppText variant="label" tone="tertiary">{t('pay_recent_bills')}</AppText>
            <AppText
              variant="bodySm"
              weight="500"
              tone="brand"
              onPress={() => router.push('/bills-history')}
            >
              {t('pay_view_all')}
            </AppText>
          </View>
          {historyLoading ? (
            <View style={{ gap: space.s2, marginTop: space.s2 }}>
              {[0, 1, 2].map((i) => <Skeleton key={i} h={48} />)}
            </View>
          ) : !history || history.length === 0 ? (
            <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>{t('pay_no_history')}</AppText>
          ) : (
            <View>
              {history.map((b, i) => <RecentRow key={b.id} bill={b} isLast={i === history.length - 1} />)}
            </View>
          )}
        </View>
      </Screen>

      {/* breakdown */}
      <Sheet visible={sheetOpen} onClose={() => setSheetOpen(false)}>
        {bill ? (
          <>
            <AppText variant="headingMd" weight="500" style={{ marginBottom: space.s4 }}>
              {t('pay_breakdown', { month: monthYear(bill.period) })}
            </AppText>
            <View style={{ gap: 14 }}>
              {bill.breakdown.map((line) => (
                <View key={line.label} style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                  <AppText variant="bodyMd" tone="secondary">{line.label}</AppText>
                  <AppText variant="bodyMd" mono>{inr(line.amount)}</AppText>
                </View>
              ))}
              <Hairline />
              <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <AppText variant="bodyMd" tone="secondary">{t('pay_subtotal')}</AppText>
                <AppText variant="bodyMd" mono>{inr(bill.amount)}</AppText>
              </View>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <AppText variant="bodyMd" tone="secondary">{t('pay_late_fee')}</AppText>
                <AppText variant="bodyMd" mono tone={bill.lateFee > 0 ? 'primary' : 'tertiary'}>
                  {inr(bill.lateFee)}
                </AppText>
              </View>
              <Hairline />
              <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                <AppText variant="bodyLg" weight="600">{t('pay_total_due')}</AppText>
                <AppText variant="bodyLg" weight="600" mono>{inr(bill.totalAmount)}</AppText>
              </View>
            </View>
          </>
        ) : null}
      </Sheet>

      {/* treasurer: record receipt */}
      <Sheet visible={recordFor !== null} onClose={() => setRecordFor(null)}>
        {recordFor ? (
          <>
            <AppText variant="headingMd" weight="500">
              {t('pay_record_title', { amount: inr(recordFor.totalAmount), flat: recordFor.flatCode ?? t('pay_flat') })}
            </AppText>
            <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s2 }}>
              {t('pay_record_sub', { month: monthYear(recordFor.period) })}
            </AppText>
            <AppText variant="bodySm" weight="500" tone="secondary" style={{ marginTop: space.s4, marginBottom: space.s2 }}>
              {t('pay_received_via')}
            </AppText>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2 }}>
              {RECORD_METHODS.map((m) => (
                <Chip key={m} label={methodLabel[m]} active={method === m} onPress={() => setMethod(m)} />
              ))}
            </View>
            <View style={{ marginTop: space.s4 }}>
              <Field label={t('pay_reference')} help={t('pay_reference_help')}>
                <Input value={reference} onChangeText={setReference} placeholder="e.g. UTR-1234567890" maxLength={60} />
              </Field>
            </View>
            <Button
              title={recordBusy ? t('pay_recording') : t('pay_record_received')}
              disabled={recordBusy}
              style={{ marginTop: space.s4 }}
              onPress={doRecord}
            />
          </>
        ) : null}
      </Sheet>
    </View>
  );
}

function PayToRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      <AppText variant="bodySm" tone="tertiary" style={{ width: 76 }}>{label}</AppText>
      <AppText variant="bodyMd" mono weight="500" style={{ flex: 1 }} selectable>{value}</AppText>
    </View>
  );
}

/* ---------------- mobile hero: tinted band, big mono due amount ---------------- */
function HeroBand({ bill, flatCode }: { bill: Bill; flatCode?: string }) {
  const { colors } = useTheme();
  const { t } = useT();
  const decimals = bill.totalAmount % 100 !== 0 ? 2 : 0;
  return (
    <View style={{ marginHorizontal: -space.s4, marginTop: -space.s5, marginBottom: 0, position: 'relative', overflow: 'hidden' }}>
      <LinearGradient
        colors={[colors.headerBandTo, colors.headerBandFrom, colors.canvas0]}
        locations={[0, 0.55, 1]}
        style={StyleSheet.absoluteFill}
      />
      <View style={{ paddingHorizontal: space.s4, paddingTop: space.s5, paddingBottom: space.s6 }}>
      {flatCode ? (
        <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
          <View style={{
            borderWidth: StyleSheet.hairlineWidth, borderColor: colors.border, borderRadius: radius.full,
            paddingHorizontal: space.s3, paddingVertical: 6, backgroundColor: colors.canvas0,
          }}>
            <AppText mono weight="500" style={{ fontSize: 12, color: colors.textSecondary }}>{flatCode}</AppText>
          </View>
        </View>
      ) : null}
      <AppText variant="label" tone="tertiary" style={{ marginTop: space.s3 }}>{t('pay_due_by', { date: shortDate(bill.dueDate) })}</AppText>
      <CountUp
        value={bill.totalAmount / 100}
        format={(n) => formatINR(n, { decimals })}
        mono weight="500"
        style={{ fontSize: 44, lineHeight: 50, letterSpacing: -0.4, marginTop: 4, color: colors.textPrimary }}
      />
      <AppText variant="bodySm" tone="secondary" style={{ marginTop: 2 }}>
        {monthYear(bill.period)} · {t('pay_invoice_label')} <AppText mono variant="bodySm" tone="secondary">{invoiceCode(bill.period, bill.id)}</AppText>
      </AppText>
      {bill.lateFee > 0 ? (
        <AppText variant="bodySm" style={{ color: colors.warning500, marginTop: 4 }}>
          {t('pay_includes_late', { amount: inr(bill.lateFee) })}
        </AppText>
      ) : null}
      </View>
    </View>
  );
}

function RecentRow({ bill, isLast }: { bill: Bill; isLast: boolean }) {
  const { colors } = useTheme();
  const { t } = useT();
  const paid = bill.status === 'PAID';
  const sub = paid && bill.payments[0]
    ? t('pay_recorded_on', { date: shortDate(bill.payments[0].paidAt) })
    : bill.status === 'OVERDUE'
      ? t('pay_overdue_since', { date: shortDate(bill.dueDate) })
      : t('pay_due_by', { date: shortDate(bill.dueDate) });
  return (
    <View>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 12 }}>
        <View>
          <AppText variant="bodyMd" weight="500">{monthYear(bill.period)}</AppText>
          <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{sub}</AppText>
          {bill.lateFee > 0 ? (
            <AppText variant="label" style={{ color: colors.warning500, marginTop: 2 }}>{t('pay_plus_late')}</AppText>
          ) : null}
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <AppText variant="headingSm" weight="500" mono>{inr(bill.totalAmount)}</AppText>
          {paid ? <Icon name="check-circle" size={16} color={colors.success500} /> : <Pill status={billPillStatus(bill.status)} label={billPillLabel(bill.status)} />}
        </View>
      </View>
      {!isLast ? <Hairline /> : null}
    </View>
  );
}

/* ---------------- desktop: bill table + due card + how-to-pay ---------------- */
function DesktopPayments({ bill, history, historyLoading, payToCard }: {
  bill: Bill; history: Bill[]; historyLoading: boolean; payToCard: React.ReactNode;
}) {
  const { colors } = useTheme();
  const { t } = useT();
  return (
    <View style={{ flexDirection: 'row', gap: space.s6, padding: space.s6, alignItems: 'flex-start' }}>
      <View style={{ flex: 1.4, minWidth: 0 }}>
        <AppText variant="headingLg" display style={{ marginBottom: space.s4 }}>{t('pay_title')}</AppText>
        <View style={{
          borderWidth: StyleSheet.hairlineWidth, borderColor: colors.borderSoft, borderRadius: radius.xl,
          backgroundColor: colors.canvas0, overflow: 'hidden',
        }}>
          <View style={{
            flexDirection: 'row', paddingVertical: space.s3, paddingHorizontal: space.s5,
            borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.borderSoft,
          }}>
            <AppText variant="label" tone="tertiary" style={{ flex: 1.4 }}>{t('pay_col_invoice')}</AppText>
            <AppText variant="label" tone="tertiary" style={{ flex: 1 }}>{t('pay_col_period')}</AppText>
            <AppText variant="label" tone="tertiary" style={{ flex: 1, textAlign: 'right' }}>{t('pay_col_amount')}</AppText>
            <AppText variant="label" tone="tertiary" style={{ flex: 0.8, textAlign: 'right' }}>{t('pay_col_status')}</AppText>
          </View>
          {historyLoading ? (
            <View style={{ padding: space.s5, gap: space.s3 }}>
              {[0, 1, 2, 3].map((i) => <Skeleton key={i} h={20} />)}
            </View>
          ) : history.length === 0 ? (
            <View style={{ padding: space.s5 }}>
              <AppText variant="bodySm" tone="tertiary">{t('pay_no_history')}</AppText>
            </View>
          ) : (
            history.map((b, i) => <TableRow key={b.id} bill={b} isLast={i === history.length - 1} />)
          )}
        </View>
      </View>

      <View style={{ width: 340, gap: space.s4 }}>
        <View style={{
          borderWidth: StyleSheet.hairlineWidth, borderColor: colors.borderSoft, borderRadius: radius.xl,
          backgroundColor: colors.canvas0, padding: space.s6,
        }}>
          <AppText variant="label" tone="tertiary">{t('pay_due_by', { date: shortDate(bill.dueDate) })}</AppText>
          <AppText mono weight="500" style={{ fontSize: 38, lineHeight: 44, marginTop: space.s2 }}>
            {inr(bill.totalAmount)}
          </AppText>
          <View style={{ marginTop: space.s4 }}>
            {bill.breakdown.map((line) => (
              <View key={line.label} style={{
                flexDirection: 'row', justifyContent: 'space-between', paddingVertical: space.s2,
                borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.borderSoft,
              }}>
                <AppText variant="bodySm" tone="secondary">{line.label}</AppText>
                <AppText variant="bodySm" mono>{inr(line.amount)}</AppText>
              </View>
            ))}
          </View>
        </View>
        {payToCard}
      </View>
    </View>
  );
}

function TableRow({ bill, isLast }: { bill: Bill; isLast: boolean }) {
  const { colors } = useTheme();
  return (
    <View style={{
      flexDirection: 'row', alignItems: 'center', paddingVertical: space.s3, paddingHorizontal: space.s5,
      borderBottomWidth: isLast ? 0 : StyleSheet.hairlineWidth, borderBottomColor: colors.borderSoft,
    }}>
      <AppText mono style={{ flex: 1.4, fontSize: 12, color: colors.textSecondary }}>{invoiceCode(bill.period, bill.id)}</AppText>
      <AppText variant="bodySm" style={{ flex: 1 }}>{monthYear(bill.period)}</AppText>
      <AppText mono variant="bodySm" style={{ flex: 1, textAlign: 'right' }}>{inr(bill.totalAmount)}</AppText>
      <View style={{ flex: 0.8, alignItems: 'flex-end' }}>
        <Pill status={billPillStatus(bill.status)} label={billPillLabel(bill.status)} />
      </View>
    </View>
  );
}
